import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './FeedbackPopup.module.css';

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface PendingForm {
  id: number;
  title: string;
  description: string | null;
  isAnonymous: boolean;
  endDate: string | null;
}

/* ─── Deferral persistence ───────────────────────────────────────────────── */
const DEFERRED_KEY = 'njord_feedback_deferred';

interface DeferredEntry { laterAt: number; reminderShown: boolean; }

const getDeferredStore = (): Record<number, DeferredEntry> => {
  try { return JSON.parse(localStorage.getItem(DEFERRED_KEY) || '{}'); } catch { return {}; }
};

const saveDeferredStore = (store: Record<number, DeferredEntry>) => {
  localStorage.setItem(DEFERRED_KEY, JSON.stringify(store));
};

interface Question {
  id: number;
  questionText: string;
  questionType: 'multiple_choice' | 'text' | 'rating' | 'checkbox';
  options: string[] | null;
  required: boolean;
}

interface FormData {
  id: number;
  title: string;
  description: string | null;
  isAnonymous: boolean;
  questions: Question[];
}

interface FeedbackPopupProps {
  onDismiss: () => void;
  onDefer: (formId: number, formTitle: string) => void;
  onFormFound: (formId: number, formTitle: string) => void;
  /** When true (user explicitly clicked "Give Feedback"), skip deferral check and always show the form */
  forceOpen?: boolean;
}

/* ─── Component ──────────────────────────────────────────────────────────── */
const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ onDismiss, onDefer, onFormFound, forceOpen }) => {
  const [pendingForm, setPendingForm] = useState<PendingForm | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flashQuestionId, setFlashQuestionId] = useState<number | null>(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get('/api/feedback/pending', { withCredentials: true });
      if (!res.data || res.data.length === 0) { onDismiss(); return; }

      const form: PendingForm = res.data[0];
      onFormFound(form.id, form.title);

      // If opened explicitly by the user, skip deferral logic and always show.
      if (forceOpen) { setPendingForm(form); return; }

      const store = getDeferredStore();
      const entry = store[form.id];

      if (entry) {
        if (entry.reminderShown) {
          // User already got the <24h last-chance nudge and deferred again
          // → no auto-popup, but keep the header button so they can still reach it
          onDefer(form.id, form.title); return;
        }
        // Check if we're within 24h of expiry
        if (form.endDate) {
          const msLeft = new Date(form.endDate).getTime() - Date.now();
          if (msLeft > 0 && msLeft < 24 * 60 * 60 * 1000) {
            // Show popup one last time; mark so we don't auto-popup again
            store[form.id] = { ...entry, reminderShown: true };
            saveDeferredStore(store);
            setPendingForm(form); return;
          }
        }
        // Not within 24h window → no auto-popup, keep header button
        onDefer(form.id, form.title); return;
      }

      // Never deferred → show popup
      setPendingForm(form);
    } catch {
      onDismiss();
    }
  }, [onDismiss, onDefer, onFormFound, forceOpen]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleAnswerNow = async () => {
    if (!pendingForm) return;
    try {
      const res = await axios.get(`/api/feedback/forms/${pendingForm.id}`, { withCredentials: true });
      setFormData(res.data);
      setShowForm(true);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // Already submitted — show thank you then dismiss
        setSubmitted(true);
        setShowForm(true);
        setTimeout(() => onDismiss(), 2000);
      }
    }
  };

  const handleLater = () => {
    if (pendingForm) {
      const store = getDeferredStore();
      const existing = store[pendingForm.id];
      store[pendingForm.id] = {
        laterAt: Date.now(),
        // Preserve reminderShown if it was already set (deferred during <24h window)
        reminderShown: existing?.reminderShown ?? false,
      };
      saveDeferredStore(store);
      onDefer(pendingForm.id, pendingForm.title);
    }
  };

  const setAnswer = (questionId: number, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const toggleCheckbox = (questionId: number, option: string) => {
    setAnswers(prev => {
      const current: string[] = prev[questionId] || [];
      const updated = current.includes(option) ? current.filter((o: string) => o !== option) : [...current, option];
      return { ...prev, [questionId]: updated };
    });
  };

  const handleSubmit = async () => {
    if (!formData) return;
    setError(null);

    // Validate required
    for (const q of formData.questions) {
      if (q.required) {
        const a = answers[q.id];
        if (a === undefined || a === null || a === '' || (Array.isArray(a) && a.length === 0)) {
          setError(`Please answer: "${q.questionText}"`);
          setFlashQuestionId(q.id);
          setTimeout(() => setFlashQuestionId(null), 1200);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const payload = formData.questions.map(q => {
        const a = answers[q.id];
        return {
          questionId: q.id,
          answerText: ['multiple_choice', 'text'].includes(q.questionType) ? (a || null) : null,
          selectedOptions: q.questionType === 'checkbox' ? (a || null) : null,
          ratingValue: q.questionType === 'rating' ? (a || null) : null,
        };
      });

      await axios.post(`/api/feedback/forms/${formData.id}/submit`, { answers: payload }, { withCredentials: true });
      // Clear deferral record on successful submission
      const store = getDeferredStore();
      delete store[formData.id];
      saveDeferredStore(store);
      setSubmitted(true);
      setTimeout(() => onDismiss(), 2000);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setSubmitted(true);
        setTimeout(() => onDismiss(), 1500);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── Prompt popup (not yet answering) ─────────────────────────────────── */
  if (!showForm && pendingForm) {
    return (
      <div className={styles.overlay} onClick={handleLater}>
        <div className={styles.promptCard} onClick={e => e.stopPropagation()}>
          <div className={styles.promptHeader}>
            <h3 className={styles.promptTitle}>Your feedback is greatly appreciated!</h3>
          </div>
          <div className={styles.promptBody}>
            <p className={styles.promptFormTitle}>{pendingForm.title}</p>
            {pendingForm.description && (
              <p className={styles.promptDescription}>{pendingForm.description}</p>
            )}
            {pendingForm.isAnonymous && (
              <p className={styles.anonNotice}>Your response will be anonymous.</p>
            )}
          </div>
          <div className={styles.promptActions}>
            <button className={styles.laterBtn} onClick={handleLater}>Later</button>
            <button className={styles.answerBtn} onClick={handleAnswerNow}>Give Feedback</button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Form overlay ─────────────────────────────────────────────────────── */
  if (showForm && formData) {
    return (
      <div className={styles.overlay}>
        <div className={styles.formCard} onClick={e => e.stopPropagation()}>
          {submitted ? (
            <div className={styles.thankYou}>
              <h3 className={styles.thankYouTitle}>Thank you for your feedback</h3>
              <p className={styles.thankYouText}>Your response has been recorded.</p>
            </div>
          ) : (
            <>
              <div className={styles.formHeader}>
                <h3 className={styles.formTitle}>{formData.title}</h3>
                {formData.description && <p className={styles.formDescription}>{formData.description}</p>}
                {formData.isAnonymous && <p className={styles.anonNotice}>🔒 Your responses are anonymous.</p>}
              </div>

              <div className={styles.formBody}>
                {formData.questions.map((q, idx) => (
                  <div key={q.id} className={styles.questionBlock}>
                    <label className={`${styles.questionLabel}${flashQuestionId === q.id ? ` ${styles.questionLabelFlash}` : ''}`}>
                      <span className={styles.questionIdx}>Q{idx + 1}.</span>
                      {q.questionText}
                      {q.required && <span className={styles.requiredMark}>*</span>}
                    </label>

                    {q.questionType === 'text' && (
                      <textarea
                        className={styles.textInput}
                        value={answers[q.id] || ''}
                        onChange={e => setAnswer(q.id, e.target.value)}
                        placeholder="Type your answer..."
                        rows={3}
                      />
                    )}

                    {q.questionType === 'multiple_choice' && q.options && (
                      <div className={styles.optionsList}>
                        {q.options.map(opt => (
                          <label key={opt} className={styles.radioLabel}>
                            <input
                              type="radio"
                              name={`question_${q.id}`}
                              value={opt}
                              checked={answers[q.id] === opt}
                              onChange={() => setAnswer(q.id, opt)}
                            />
                            <span className={styles.radioCustom} />
                            <span className={styles.optionText}>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {q.questionType === 'checkbox' && q.options && (
                      <div className={styles.optionsList}>
                        {q.options.map(opt => (
                          <label key={opt} className={styles.checkLabel}>
                            <input
                              type="checkbox"
                              checked={(answers[q.id] || []).includes(opt)}
                              onChange={() => toggleCheckbox(q.id, opt)}
                            />
                            <span className={styles.optionText}>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {q.questionType === 'rating' && (
                      <div className={styles.ratingGroup}>
                        <div className={styles.ratingRow}>
                          {[1, 2, 3, 4, 5].map(v => (
                            <button
                              key={v}
                              className={`${styles.ratingBtn} ${answers[q.id] === v ? styles.ratingBtnActive : ''}`}
                              onClick={() => setAnswer(q.id, v)}
                              type="button"
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                        <span className={styles.ratingScale}>1 = Low &nbsp;·&nbsp; 5 = High</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {error && <p className={styles.errorMsg}>{error}</p>}

              <div className={styles.formActions}>
                <button className={styles.laterBtn} onClick={handleLater}>Do it later</button>
                <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default FeedbackPopup;
